import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BenchObjectTableLastCell } from 'components/benchObject/BenchObjectTable/ui';
import { useGetTokenSingleSaleDetails } from 'hooks/offers/useGetTokenSingleSaleDetails';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import { StartingPrice } from '../StartingPrice';
import { StartingPriceCell_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: StartingPriceCell_anyPlayer;
  rarity: Rarity;
  onListingsClick: () => void;
};

export const StartingPriceCell = ({
  anyPlayer,
  rarity,
  onListingsClick,
}: Props) => {
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const { lowestPriceAnyCard } = anyPlayer;
  const offer = useGetTokenSingleSaleDetails(lowestPriceAnyCard);
  const primaryBuy = useIsPrimaryBuyLive(lowestPriceAnyCard);
  const monetaryAmount = (primaryBuy || offer)?.price;
  return (
    <BenchObjectTableLastCell isLaptopOrAbove={isLaptopOrAbove}>
      {monetaryAmount && (
        <StartingPrice
          small={!isLaptopOrAbove}
          column={!isLaptopOrAbove}
          monetaryAmount={monetaryAmount}
        />
      )}

      {isLaptopOrAbove && (
        <Horizontal gap={1}>
          <Button
            color="secondary"
            size="small"
            role="link"
            startIcon={<NewScarcityIcon rarity={rarity} size={2} />}
            onClick={onListingsClick}
          >
            <FormattedMessage {...glossary.listings} />
          </Button>
        </Horizontal>
      )}
    </BenchObjectTableLastCell>
  );
};

StartingPriceCell.fragments = {
  anyPlayer: gql`
    fragment StartingPriceCell_anyPlayer on AnyPlayerInterface {
      slug
      lowestPriceAnyCard(rarity: $rarity, inSeason: $inSeason) {
        slug
        rarityTyped
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
    }
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
  ` as TypedDocumentNode<StartingPriceCell_anyPlayer>,
};
